import { SwiperSectionProps, useCaseType } from '@rimactechnology/rimac-ui-lib';
import dynamic from 'next/dynamic';

import {
  FooterQuery,
  FormQuery,
  HeroVideoSectionFragment,
  ImageCardsWithLinkFragment,
  LargeImageSectionFragment,
  NavigationQuery,
  PartnersBlogSectionFragment,
  SeoFragment,
} from '@/api';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import { Seo } from '@/components/Seo';
import HeroVideoSection from '@/components/shared/HeroVideoSection';
import LargeImage from '@/components/shared/LargeImage';
import getCdlPublicId from '@/hooks/useCdlPublicId';

const SwiperSection = dynamic(
  () =>
    import('@rimactechnology/rimac-ui-lib').then((mod) => mod.SwiperSection),
  {
    ssr: false,
  },
);

type Props = {
  seo?: SeoFragment | null;
  hero?: HeroVideoSectionFragment | null;
  cards?: ImageCardsWithLinkFragment | null;
  largeImageSection?: LargeImageSectionFragment | null;
  partnersBlogSection?: PartnersBlogSectionFragment | null;
  formData: FormQuery;
  footerData: FooterQuery;
  headerData: NavigationQuery;
};

export const Homepage = ({
  seo,
  hero,
  cards,
  largeImageSection,
  partnersBlogSection,
  formData,
  footerData,
  headerData,
}: Props) => {
  const cardsProps: SwiperSectionProps = {
    isControls: true,
    cards: (cards?.imageCardLink || []).map((card) => ({
      image: {
        imgSrc: getCdlPublicId(card?.img_desktop.data?.attributes?.url || ''),
        imgSrcMobile: getCdlPublicId(
          card?.img_mobil?.data?.attributes?.url || undefined,
        ),
        alt: card?.alt || '',
        blurHash: card?.img_desktop.data?.attributes?.blurhash || undefined,
        link: card?.link_path || '',
      },
      title: card?.title || '',
      text: card?.description || '',
      path: card?.link_path || '',
    })),
    nonLazyLoadImageCount: 0,
    useCase: 'technology' as useCaseType,
    anchor: cards?.anchor || undefined,
  };

  return (
    <Layout formData={formData} footerData={footerData} headerData={headerData}>
      <Seo seo={seo} />
      <HeroVideoSection hero={hero} />
      <SwiperSection {...cardsProps} />
      <LargeImage
        largeImageSection={largeImageSection}
        titleMaxWidth={576}
        descriptionMaxWidth={760}
      />
      <Partners data={partnersBlogSection} />
    </Layout>
  );
};
